import { Component, type ErrorInfo, type ReactNode, useEffect, useState } from 'react';
import { PlanetScene } from './planet/PlanetScene';
import { HUDController } from './planet/HUDController';
import {
  useScrollTimeline,
  type ScrollTimelineState,
} from './planet/useScrollTimeline';
import { usePlanetDrag, type PlanetDragState } from './planet/usePlanetDrag';
import { useSwipeGestures } from './planet/useSwipeGestures';
import { useModeController } from './planet/useModeController';
import { useModeKeyboard } from './planet/useModeKeyboard';
import { useTourController } from './planet/useTourController';
import { useUrlSync } from './planet/useUrlSync';
import { SCENES, PAGE_HEIGHT_VH } from './planet/planetSections';
import { LoadingSplash } from './planet/LoadingSplash';
import { PrintFallback } from './PrintFallback';
import { CursorFollower } from './components/ui/CursorFollower';
import { NotFound } from './NotFound';
import { onSceneEnter } from './utils/analytics';
import './App.css';

/**
 * App — single page that owns:
 *
 *   - the loading splash (covers the scene until R3F is live)
 *   - the body height (700vh of empty scrollable space)
 *   - the fixed full-viewport WebGL canvas (the planet)
 *   - the HUD overlay (panels, nav, scroll cue)
 *   - the print-fallback container (hidden on screen, printed on paper)
 *   - the global scroll-timeline + drag + swipe subscriptions
 *
 * Old 2D sections are gone — every chapter is now a scene in the
 * planet and lives entirely inside <PlanetScene /> and the HUD.
 */
export default function App() {
  // Mount the scroll timeline at the root. The hook installs the
  // scroll listener + rAF smoothing loop and writes window.__planetProgress
  // each frame so the R3F scene graph can read it without re-rendering.
  const scroll: ScrollTimelineState = useScrollTimeline();

  // Mount the drag controller. During drag, the planet spins freely
  // and the camera holds position; on release, both lerp back.
  const drag: PlanetDragState = usePlanetDrag();

  // Mount the mobile swipe-to-next-scene gesture handler.
  useSwipeGestures();

  // Mount the mode controller (narrative / exploration / tour) +
  // global keyboard shortcuts (E, T, R, ESC, 1-7, SPACE).
  const modeController = useModeController();
  useModeKeyboard();
  // Mount the auto-tour controller. Listens for mode changes into /
  // out of 'tour' and runs the passive reel.
  useTourController();

  // Mount the URL hash + document.title sync. Debounced; writes
  // `#scene=KEY` (or `#explore`) as the user scrolls, and restores
  // the original title on `beforeunload`.
  useUrlSync();

  // Expose the controller globally so the ModeToggle pill (which lives
  // inside the HUD layer) can call into the same setMode implementation
  // without prop-drilling through the React tree.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__planetModeController = {
        toggle: modeController.toggle,
        setMode: modeController.setMode,
      };
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.__planetModeController = undefined;
      }
    };
  }, [modeController]);

  // canvasReady flips to true once R3F's <Canvas onCreated> callback
  // has fired. The LoadingSplash watches this and fades out as soon
  // as it (and useWebGLAvailable) are both true.
  const [canvasReady, setCanvasReady] = useState(false);
  const [sceneFailed, setSceneFailed] = useState(false);

  // True when the user navigated to an unknown URL hash (e.g.
  // #scene=unknown). Renders a small toast inside the HUD layer.
  const [unknownScene, setUnknownScene] = useState<string | null>(null);

  // ---- Scene-enter analytics ---------------------------------------
  // Emit scene_enter whenever the active scene index changes. The
  // listener also detects unsupported URL hashes (#scene=foo) and
  // surfaces a 404 toast.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let lastIdx = window.__planetSceneIndex ?? 0;
    const id = window.setInterval(() => {
      const cur = window.__planetSceneIndex ?? 0;
      if (cur !== lastIdx) {
        lastIdx = cur;
        const scene = SCENES[cur];
        if (scene) onSceneEnter(scene.key);
      }
    }, 250);

    // ----- Unknown hash detection -----
    const parseSceneHash = () => {
      const hash = window.location.hash.replace(/^#/, '');
      const match = hash.match(/^scene=([^&]+)/);
      if (!match) return;
      const key = match[1];
      const exists = SCENES.some((s) => s.key === key);
      if (!exists) {
        setUnknownScene(key);
      } else {
        setUnknownScene(null);
      }
    };
    parseSceneHash();
    window.addEventListener('hashchange', parseSceneHash);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('hashchange', parseSceneHash);
    };
  }, []);

  // Custom-cursor class toggle (kept from the original 2D site).
  useEffect(() => {
    const mq = window.matchMedia('(hover: none), (max-width: 860px)');
    const update = () => {
      document.body.classList.toggle('has-custom-cursor', !mq.matches);
    };
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Apply the 700vh body height once on mount. The scroll listener
  // reads document.documentElement.scrollHeight which reflects this.
  useEffect(() => {
    const apply = () => {
      document.body.style.minHeight = `${PAGE_HEIGHT_VH}vh`;
      // Force a re-measure of scrollHeight after layout settles so the
      // scroll timeline gets an honest target on first paint.
      requestAnimationFrame(() => {
        if (typeof window === 'undefined') return;
        window.dispatchEvent(new Event('resize'));
      });
    };
    apply();
    window.addEventListener('load', apply);
    return () => window.removeEventListener('load', apply);
  }, []);

  // Touch the refs once so TS doesn't complain about unused locals —
  // these are read by the children, not by App itself.
  void scroll;
  void drag;

  return (
    <div className="app">
      {/* The loading splash. First child so it sits on top of every
          other layer during the initial mount. Fades out + unmounts
          itself once WebGL + Canvas ready both report true. */}
      <LoadingSplash canvasReady={canvasReady || sceneFailed} />

      {/* The fixed WebGL canvas. Sits behind every HUD overlay. */}
      {sceneFailed ? (
        <SceneFallback />
      ) : (
        <SceneErrorBoundary onError={() => setSceneFailed(true)}>
          <PlanetScene
            dragDeltaRef={drag.deltaRotation}
            isDraggingRef={drag.isDragging}
            onCanvasReady={() => setCanvasReady(true)}
          />
        </SceneErrorBoundary>
      )}

      {/* The HUD overlay (chapter nav, panels, scroll cue). */}
      <HUDController />

      {/* The custom cursor follower (kept from the 2D site). */}
      <CursorFollower />

      {/* Unknown-scene toast. Rendered only when a bad URL hash is
          detected; auto-dismisses after 3s. */}
      {unknownScene && (
        <NotFound
          toastMs={3000}
          title="scene not found"
          body={`No scene named "${unknownScene}" — try the planet.`}
          onToastDone={() => setUnknownScene(null)}
        />
      )}

      {/* Print-only text container. Hidden on screen via .print-fallback
          (display: none); revealed by @media print. Lives in the React
          tree so search engines and screen readers see the same content
          the planet scene animates. */}
      <PrintFallback />
    </div>
  );
}

function SceneFallback() {
  return (
    <div
      className="planet-scene planet-scene--fallback planet-scene--recovered"
      aria-hidden="true"
    />
  );
}

class SceneErrorBoundary extends Component<
  { children: ReactNode; onError?: () => void },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.();
    if (typeof window !== 'undefined') {
      window.__blogSceneError = {
        message: error.message,
        stack: error.stack,
        componentStack: info.componentStack ?? '',
        at: new Date().toISOString(),
      };
    }
  }

  render() {
    if (this.state.failed) {
      return <SceneFallback />;
    }

    return this.props.children;
  }
}

declare global {
  interface Window {
    __blogSceneError?: {
      at: string;
      componentStack: string;
      message: string;
      stack?: string;
    };
  }
}
